import { Vec2 } from "utils/math/vec2";
import { Webgl2dRenderer } from "../../../webgl2d-renderer";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";
import { CropSelectionComponent } from "./crop-selection-component";
import { CropUnitComponent } from "./crop-unit-component";
import type { Nullable } from "models-webgl2";
import { ImageAdjustRectComponent } from "../image-adjust-rect-component";
import { ImageRectComponent } from "../image-rect-component";
import { Mat3 } from "utils/math/mat3";

export class CropSelectionBottomComponent extends Webgl2Component {
  private _tm3: Mat3;
  private _cropTopLeft: Vec2;

  private get _selectedComponent(): Nullable<ImageRectComponent | ImageAdjustRectComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof ImageRectComponent || component instanceof ImageAdjustRectComponent
      ? component
      : null;
  }

  constructor(
    private readonly _renderer: Webgl2dRenderer,
    private readonly _cropSelectionComponent: CropSelectionComponent,
  ) {
    super();

    const bottomUnit = new CropUnitComponent(new Vec2(16, 4));
    bottomUnit.style.top = "50%";
    bottomUnit.style.translateY = "-50%";
    bottomUnit.style.pointerEvents = "none";
    this.appendChild(bottomUnit);

    this.transform.setSize(16, 16);
  }

  public onMouseDown() {
    if (!this._selectedComponent) return;

    const selectedTopLeft = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._selectedComponent,
      Vec2.zero(),
    );
    const cropTopLeft = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._cropSelectionComponent,
      Vec2.zero(),
    );
    this._tm3 = Mat3.identity()
      .rotate(-this._selectedComponent.transform.rads)
      .translate(selectedTopLeft.clone().negate());
    this._cropTopLeft = cropTopLeft.transformMat3(this._tm3.mat);
  }

  public onMouseMove(e: MouseEvent | TouchEvent) {
    const currentMousePos = this._renderer.camera
      .getGlobalMousePixelPosition(e)
      .transformMat3(this._tm3.mat)
      .clampY(this._cropTopLeft.y, Infinity);

    const newSize = new Vec2(
      this._cropSelectionComponent.transform.size.x,
      currentMousePos.y - this._cropTopLeft.y,
    );

    this._cropSelectionComponent.transform.calcDimensions({
      size: newSize,
      position: new Vec2(
        this._cropTopLeft.x + newSize.x / 2,
        (this._cropTopLeft.y + currentMousePos.y) / 2,
      ).transformMat3(this._tm3.clone().invert().mat),
    });
    this._renderer.render();
  }

  public getRenderData() {
    const material = Webgl2.materials.transparent;
    return { material };
  }
}

export default CropSelectionBottomComponent;
